import { useMemo, useCallback } from 'react'
import { useTheme } from '../context/ThemeContext'
import {
    LineChart, Line, BarChart, Bar, Cell, LabelList,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, Brush, ResponsiveContainer
} from 'recharts'

function getBarColor(closeBBL) {
    if (closeBBL >= 250) return '#DC2626'
    if (closeBBL >= 200) return '#F59E0B'
    return '#16a34a'
}

function fmtK(n) {
    const abs = Math.abs(n)
    if (abs >= 10000) return `${(n / 1000).toFixed(2)}K`
    if (abs >= 1000) return `${(n / 1000).toFixed(1)}K`
    return Math.round(n).toLocaleString()
}

function TankOverview({ kpis, trends, tanks, fleetTrend }) {
    const { theme } = useTheme()
    const isDark = theme === 'dark'

    const loadableOil = kpis?.loadableOilBBL ?? 0
    const totalOil = kpis?.totalOilBBL ?? 0
    const vsYesterday = trends?.vsYesterday ?? 0
    const vsLastWeek = trends?.vsLastWeek ?? 0

    // Alert tanks (needs truck: >=180 BBL and >=50% full)
    const alertTanks = useMemo(() => {
        if (!tanks) return []
        return tanks
            .filter(t => t.closeBBL >= 180 && t.percentFull >= 50)
            .sort((a, b) => (b.percentFull || 0) - (a.percentFull || 0))
    }, [tanks])

    // Top tanks by volume for horizontal bar chart
    const topTanks = useMemo(() => {
        if (!tanks) return []
        return tanks
            .sort((a, b) => (b.closeBBL || 0) - (a.closeBBL || 0))
            .slice(0, 25)
            .map(t => ({
                name: `${t.parentUnitName} - ${t.tankName}`.length > 30
                    ? `${t.parentUnitName} - ${t.tankName}`.substring(0, 28) + '...'
                    : `${t.parentUnitName} - ${t.tankName}`,
                fullName: `${t.parentUnitName} - ${t.tankName}`,
                value: Math.round(t.closeBBL || 0),
                color: getBarColor(t.closeBBL || 0)
            }))
    }, [tanks])

    // Compute 7-day moving averages for fleet trend
    const trendWithMA = useMemo(() => {
        if (!fleetTrend || fleetTrend.length === 0) return []
        const WINDOW = 7
        return fleetTrend.map((row, i) => {
            if (i < WINDOW - 1) return { ...row, totalMA: null, loadableMA: null }
            let sumTotal = 0, sumLoadable = 0
            for (let j = i - WINDOW + 1; j <= i; j++) {
                sumTotal += fleetTrend[j].totalOilBBL || 0
                sumLoadable += fleetTrend[j].loadableOilBBL || 0
            }
            return {
                ...row,
                totalMA: Math.round(sumTotal / WINDOW),
                loadableMA: Math.round(sumLoadable / WINDOW),
            }
        })
    }, [fleetTrend])

    const downloadInventoryCSV = useCallback(() => {
        if (!trendWithMA || trendWithMA.length === 0) return
        const headers = ['Date', 'Total Oil BBL', 'Loadable Oil BBL', 'Total 7d Avg', 'Loadable 7d Avg']
        const rows = trendWithMA.map(r => [
            r.date,
            Math.round(r.totalOilBBL || 0),
            Math.round(r.loadableOilBBL || 0),
            r.totalMA != null ? Math.round(r.totalMA) : '',
            r.loadableMA != null ? Math.round(r.loadableMA) : '',
        ].join(','))
        const csv = [headers.join(','), ...rows].join('\n')
        const blob = new Blob([csv], { type: 'text/csv' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `Aviator_Oil_Inventory_${new Date().toISOString().slice(0, 10)}.csv`
        a.click()
        URL.revokeObjectURL(url)
    }, [trendWithMA])

    // 6 green KPI cards matching Power BI layout
    const kpiDefs = [
        { label: 'Loadable Oil', value: fmtK(loadableOil), sub: `of ${fmtK(totalOil)} total BBL` },
        { label: 'Tank Count', value: (kpis?.activeTanks || 0).toString() },
        { label: `Critical Tanks (\u2265250)`, value: (kpis?.critical || 0).toString() },
        { label: `Warning Tanks (\u2265200)`, value: (kpis?.warningTanks || 0).toString() },
        { label: 'vs Yesterday', value: `${vsYesterday >= 0 ? '\u25B2' : '\u25BC'} ${fmtK(Math.abs(vsYesterday))}` },
        { label: 'vs Last Week', value: `${vsLastWeek >= 0 ? '\u25B2' : '\u25BC'} ${fmtK(Math.abs(vsLastWeek))}` },
    ]

    return (
        <div className="space-y-4 sm:space-y-6">
            {/* Section 1: Green KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
                {kpiDefs.map((kpi, i) => (
                    <div key={i} className="bg-green-700 rounded-lg p-3 sm:p-4 text-center">
                        <p className="text-white/80 text-[10px] sm:text-xs font-medium">{kpi.label}</p>
                        <p className="text-white text-xl sm:text-3xl font-bold mt-1">{kpi.value}</p>
                        {kpi.sub && (
                            <p className="text-white/60 text-[10px] mt-0.5">{kpi.sub}</p>
                        )}
                    </div>
                ))}
            </div>

            {/* Section 2: Alert Cards (left) + Top Tanks Chart (right) */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {/* Alert Cards - Tanks Needing Trucks */}
                <div className={`rounded-lg border ${isDark ? 'border-red-800 bg-red-950/20' : 'border-red-200 bg-red-50'}`}>
                    <div className="px-4 py-2 bg-red-700 rounded-t-lg">
                        <h3 className="text-sm font-semibold text-white">
                            {'\u26A0'} Tanks Needing Trucks ({alertTanks.length} tanks, {'\u2265'}180 BBL)
                        </h3>
                    </div>
                    <div className="p-3 space-y-2 max-h-[520px] overflow-y-auto">
                        {alertTanks.length === 0 ? (
                            <p className={`text-center py-4 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                No tanks ready for haul
                            </p>
                        ) : alertTanks.map(tank => {
                            const color = getBarColor(tank.closeBBL)
                            return (
                                <div
                                    key={tank.tankId}
                                    className={`rounded-lg border p-3 ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}
                                >
                                    <div className="flex justify-between items-start">
                                        <div className="min-w-0 flex-1">
                                            <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                                {tank.parentUnitName}
                                            </p>
                                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                {tank.tankName}
                                            </p>
                                            <p className="text-xs text-blue-400 font-medium mt-1">
                                                {tank.purchaser || 'Unknown'}
                                            </p>
                                        </div>
                                        <div className="text-right shrink-0 ml-2">
                                            <span className="text-2xl font-bold" style={{ color }}>
                                                {tank.percentFull?.toFixed(0)}%
                                            </span>
                                            <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                                {tank.closeBBL?.toFixed(0)} / {tank.capacityBBL?.toFixed(0)} BBL
                                            </p>
                                        </div>
                                    </div>
                                    <div className="mt-2">
                                        <div className={`h-1.5 rounded-full ${isDark ? 'bg-slate-700' : 'bg-gray-200'}`}>
                                            <div
                                                className="h-full rounded-full"
                                                style={{
                                                    width: `${Math.min(tank.percentFull || 0, 100)}%`,
                                                    backgroundColor: color,
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>

                {/* Top Tanks by Oil Volume - Horizontal Bar Chart */}
                <div className={`rounded-lg border p-4 ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                    <div className="mb-3 pb-1 border-b-2 border-green-600">
                        <h3 className="text-sm font-semibold text-green-600">Top Tanks by Oil Volume</h3>
                    </div>
                    {topTanks.length > 0 && (
                        <ResponsiveContainer width="100%" height={topTanks.length * 26 + 20}>
                            <BarChart layout="vertical" data={topTanks} margin={{ left: 0, right: 50, top: 0, bottom: 0 }}>
                                <XAxis type="number" hide />
                                <YAxis
                                    type="category"
                                    dataKey="name"
                                    width={190}
                                    tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#4b5563' }}
                                    tickLine={false}
                                    axisLine={false}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: isDark ? '#1e293b' : '#fff',
                                        border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                    }}
                                    formatter={(v, _name, props) => [`${v.toLocaleString()} BBL`, props.payload.fullName]}
                                />
                                <Bar dataKey="value" barSize={16} radius={[0, 3, 3, 0]}>
                                    <LabelList
                                        dataKey="value"
                                        position="right"
                                        style={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#374151', fontWeight: 600 }}
                                    />
                                    {topTanks.map((entry, i) => (
                                        <Cell key={i} fill={entry.color} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                    {/* Legend */}
                    <div className={`flex items-center justify-center gap-4 mt-3 pt-2 border-t text-xs ${isDark ? 'border-slate-700 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
                        <span className="flex items-center gap-1.5">
                            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: '#DC2626' }} />
                            Critical {'\u2265'}250
                        </span>
                        <span className="flex items-center gap-1.5">
                            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: '#F59E0B' }} />
                            Warning {'\u2265'}200
                        </span>
                        <span className="flex items-center gap-1.5">
                            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: '#16a34a' }} />
                            Normal {'<'}200
                        </span>
                    </div>
                </div>
            </div>

            {/* Section 3: Fleet Trend Chart - with moving averages + zoom */}
            {fleetTrend && fleetTrend.length > 0 && (
                <div className={`rounded-lg border p-4 ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                    <div className="flex items-center justify-between mb-1">
                        <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Aviator Oil Inventory (6 Months)
                        </h3>
                        <button
                            onClick={downloadInventoryCSV}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${isDark ? 'bg-slate-700 hover:bg-slate-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                            title="Download Oil Inventory as CSV"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Download CSV
                        </button>
                    </div>
                    <p className={`text-xs mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        Light lines = daily readings &bull; Bold lines = 7-day moving average &bull; Drag brush bar to zoom
                    </p>
                    <ResponsiveContainer width="100%" height={450}>
                        <LineChart data={trendWithMA} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e2e8f0'} />
                            <XAxis
                                dataKey="date"
                                tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }}
                                tickFormatter={d => {
                                    const dt = new Date(d)
                                    return `${dt.getMonth() + 1}/${dt.getDate()}`
                                }}
                            />
                            <YAxis
                                tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }}
                                tickFormatter={v => `${(v / 1000).toFixed(0)}K`}
                            />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: isDark ? '#1e293b' : '#fff',
                                    border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                }}
                                formatter={(v, name) => {
                                    if (v == null) return [null, null]
                                    const labels = {
                                        totalOilBBL: 'Total (daily)',
                                        loadableOilBBL: 'Loadable (daily)',
                                        totalMA: 'Total (7d avg)',
                                        loadableMA: 'Loadable (7d avg)',
                                    }
                                    return [`${v.toLocaleString()} BBL`, labels[name] || name]
                                }}
                                labelFormatter={d => new Date(d).toLocaleDateString()}
                            />
                            <Legend
                                formatter={(value) => {
                                    const labels = {
                                        totalOilBBL: 'Total Oil (daily)',
                                        loadableOilBBL: 'Loadable Oil (daily)',
                                        totalMA: 'Total Oil (7d avg)',
                                        loadableMA: 'Loadable Oil (7d avg)',
                                    }
                                    return labels[value] || value
                                }}
                                wrapperStyle={{ fontSize: '11px' }}
                            />
                            {/* Raw daily data - light/faded */}
                            <Line type="monotone" dataKey="totalOilBBL" stroke="#93c5fd" strokeWidth={1} dot={false} name="totalOilBBL" opacity={0.4} />
                            <Line type="monotone" dataKey="loadableOilBBL" stroke="#fcd34d" strokeWidth={1} dot={false} name="loadableOilBBL" opacity={0.4} />
                            {/* 7-day moving averages - bold/dark */}
                            <Line type="monotone" dataKey="totalMA" stroke="#2563eb" strokeWidth={3} dot={false} name="totalMA" connectNulls={false} />
                            <Line type="monotone" dataKey="loadableMA" stroke="#d97706" strokeWidth={3} dot={false} name="loadableMA" connectNulls={false} />
                            <Brush
                                dataKey="date"
                                height={30}
                                stroke={isDark ? '#475569' : '#94a3b8'}
                                fill={isDark ? '#1e293b' : '#f8fafc'}
                                tickFormatter={d => {
                                    const dt = new Date(d)
                                    return `${dt.getMonth() + 1}/${dt.getDate()}`
                                }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    )
}

export default TankOverview
